import {
	type ContractRouterClient,
	eventIterator,
	type InferContractRouterInputs,
	type InferContractRouterOutputs,
	oc,
} from "@orpc/contract";
import { z } from "zod";
import * as schemas from "./schemas";

export * from "./admin-contracts";
export * from "./plans";
export * from "./schemas";
export * from "./schemas/admin";

export const usersContract = {
	signinPreflight: oc
		.input(z.object({ email: z.email() }))
		.output(z.object({ hasPasskey: z.boolean() })),
};

export const appsContract = {
	create: oc.input(schemas.CreateAppSchema).output(schemas.AppSchema),
	list: oc.output(z.array(schemas.AppSchema)),
	detail: oc
		.input(z.object({ id: z.string() }))
		.output(schemas.AppDetailSchema),
	update: oc.input(schemas.UpdateAppSchema).output(schemas.AppSchema),
	archive: oc
		.input(z.object({ id: z.string() }))
		.output(z.object({ id: z.string() })),
	statistics: oc
		.input(schemas.AppStatisticsInputSchema)
		.output(schemas.AppStatisticsSchema),
};

export const partnersContract = {
	create: oc.input(schemas.CreatePartnerSchema).output(schemas.PartnerSchema),
	list: oc
		.input(z.object({ appId: z.string().optional() }))
		.output(z.array(schemas.PartnerSchema)),
	detail: oc.input(z.object({ id: z.string() })).output(schemas.PartnerSchema),
	update: oc.input(schemas.UpdatePartnerSchema).output(schemas.PartnerSchema),
	remove: oc
		.input(z.object({ id: z.string() }))
		.output(z.object({ id: z.string() })),
	statistics: oc
		.input(z.object({ id: z.string() }))
		.output(schemas.PartnerStatisticsSchema),
	timeSeries: oc
		.input(
			z.object({
				id: z.string(),
				days: z.number().int().min(1).max(365).default(30),
			}),
		)
		.output(schemas.PartnerTimeSeriesSchema),
	appBreakdown: oc
		.input(z.object({ id: z.string() }))
		.output(schemas.PartnerAppBreakdownSchema),
	attributions: oc
		.input(schemas.ListPartnerAttributionsInputSchema)
		.output(schemas.ListPartnerAttributionsOutputSchema),
	/**
	 * Public endpoint to get partner terms status (for ToS gate).
	 * Returns acceptedTermsAt and partnerName.
	 */
	getTermsStatus: oc
		.input(schemas.AcceptPartnerTermsInputSchema)
		.output(schemas.PartnerTermsStatusOutputSchema),
	/**
	 * Public endpoint for partners to accept global terms of service.
	 * Accepts either stripeConnectToken or referral link approval token.
	 */
	acceptTerms: oc
		.input(schemas.AcceptPartnerTermsInputSchema)
		.output(z.object({ success: z.boolean() })),
};

export const referralLinksContract = {
	create: oc
		.input(schemas.CreateReferralLinkSchema)
		.output(schemas.ReferralLinkSchema),
	list: oc
		.input(schemas.ListReferralLinksInputSchema)
		.output(schemas.ListReferralLinksOutputSchema),
	detail: oc
		.input(z.object({ id: z.string() }))
		.output(schemas.ReferralLinkSchema),
	update: oc
		.input(schemas.UpdateReferralLinkSchema)
		.output(schemas.ReferralLinkSchema),
	remove: oc
		.input(z.object({ id: z.string() }))
		.output(z.object({ id: z.string() })),
	resendApproval: oc
		.input(z.object({ id: z.string() }))
		.output(z.object({ success: z.boolean() })),
	requestTermination: oc
		.input(schemas.RequestTerminationInputSchema)
		.output(schemas.RequestTerminationOutputSchema),
	statistics: oc
		.input(z.object({ id: z.string() }))
		.output(schemas.ReferralLinkStatisticsSchema),
	subscribers: oc
		.input(schemas.ListReferralLinkSubscribersInputSchema)
		.output(schemas.ListReferralLinkSubscribersOutputSchema),
	timeSeries: oc
		.input(
			z.object({
				id: z.string(),
				days: z.number().int().min(1).max(365).default(30),
			}),
		)
		.output(schemas.ReferralLinkTimeSeriesSchema),
};

export const offerCodesContract = {
	upload: oc
		.input(schemas.UploadOfferCodesInputSchema)
		.output(schemas.OfferCodeUploadSchema),
	listUploads: oc
		.input(schemas.ListOfferCodeUploadsInputSchema)
		.output(schemas.ListOfferCodeUploadsOutputSchema),
	stats: oc
		.input(z.object({ referralLinkId: z.string() }))
		.output(schemas.OfferCodeStatsSchema),
	deleteUnclaimed: oc
		.input(z.object({ referralLinkId: z.string() }))
		.output(z.object({ deletedCount: z.number() })),
};

export const stripeConnectionContract = {
	create: oc
		.input(schemas.CreateStripeConnectionSchema)
		.output(schemas.StripeOnboardingResponseSchema),
	read: oc
		.input(z.object({ partnerId: z.string() }))
		.output(schemas.StripeConnectionStatusSchema),
	update: oc
		.input(schemas.CreateStripeConnectionSchema)
		.output(schemas.StripeOnboardingResponseSchema),
	signup: oc.input(z.object({ token: z.string() })).output(
		z.object({
			redirect: z.string().optional(),
			requiresTermsAcceptance: z.boolean(),
			partnerName: z.string().optional(),
		}),
	),
	acceptTerms: oc
		.input(z.object({ token: z.string() }))
		.output(z.object({ redirect: z.string() })),
	requestAccess: oc
		.input(z.object({ email: z.string().email() }))
		.output(z.object({ success: z.boolean() })),
};

export const billingEntitiesContract = {
	list: oc.output(z.array(schemas.BillingEntityWithPaymentMethodSchema)),
	create: oc
		.input(schemas.CreateBillingEntityInputSchema)
		.output(schemas.CreateBillingEntityOutputSchema),
	rename: oc
		.input(schemas.RenameBillingEntityInputSchema)
		.output(schemas.BillingEntitySchema),
	replacePaymentMethod: oc
		.input(schemas.ReplacePaymentMethodInputSchema)
		.output(schemas.ReplacePaymentMethodOutputSchema),
	confirmPaymentMethod: oc
		.input(schemas.ConfirmPaymentMethodInputSchema)
		.output(z.object({ success: z.boolean() })),
	delete: oc
		.input(schemas.DeleteBillingEntityInputSchema)
		.output(z.object({ success: z.boolean() })),
};

export const claimContract = oc
	.route({
		method: "POST",
		path: "/v1/claim",
		description: `Claim a referral intent

Use this endpoint to attribute an app installation to a referral partner.

Attribution can happen in one of two ways, depending on the request:

1. Explicit claim (recommended)

If a \`claimCode\` is provided, the referral intent is claimed deterministically using that code.
This is the preferred and most reliable attribution method.

2. Time-window auto-claim (best-effort)

If no \`claimCode\` is provided, the request opts into time-window auto-claiming.

In this mode, BitEasy will attempt to attribute the installation to exactly one recent referral intent created shortly before install.
If no eligible intent exists — or if attribution would be ambiguous — no attribution is made.`,
		summary: "Claim referral intent",
	})
	.input(schemas.ClaimRequestSchema)
	.output(schemas.ClaimResponseSchema);

const deferPreflightContract = oc
	.route({
		method: "POST",
		path: "/v1/defer-preflight",
		description: `Issues a short-lived referral claim code that can be shown to a user before they install the app.

This is the explicit attribution entry point: the user copies or scans the code and later pastes it in-app to complete attribution.

Platform attribution (iOS / Android) is recorded separately via the \`/defer\` endpoint.
`,
		summary: "Issue referral claim code",
	})
	.input(schemas.DeferPreflightInputSchema)
	.output(schemas.DeferPreflightOutputSchema);

export const deferContract = oc
	.route({
		method: "POST",
		path: "/v1/defer",
		description: `Register a referral intent.

The intent is identified by a claim code, which is a unique identifier for the intent. Another option is to use a time based identifier.

The intent is claimed by the user who is making the request.
		`,
		summary: "Defer referral intent",
	})
	.input(schemas.DeferInputSchema)
	.output(schemas.DeferOutputSchema);

export const stripeWebhookContract = oc
	.route({
		method: "POST",
		path: "/v1/stripe",
		description: `Webhook destination for Stripe events
		
Do not call this endpoint directly. It is intended for use by Stripe only.`,
		summary: "Stripe webhook",
	})
	// Raw body, Stripe-Signature header is verified in the handler
	.input(z.any())
	// Stripe only cares about HTTP 2xx vs non-2xx
	.output(z.object({ ok: z.literal(true) }));

export const revenuecatContract = oc
	.route({
		method: "POST",
		path: "/v1/revenuecat/{appId}",
		description: `Webhook destination for RevenueCat events

The URL includes your BitEasy app ID, so configure this in RevenueCat's webhook settings:
\`https://api.biteasy.io/v1/revenuecat/{your-app-id}\`

Do not call this endpoint directly. It is intended for use by RevenueCat only.`,
		summary: "RevenueCat webhook",
	})
	.input(schemas.RevenueCatWebhookSchema.extend({ appId: z.string() }))
	.output(z.object({ ok: z.literal(true) }));

export const appleContract = oc
	.route({
		method: "POST",
		path: "/v1/apple/{appId}",
		description: `Webhook destination for Apple App Store Server Notifications

The URL includes your BitEasy app ID, so configure this in App Store Connect:
\`https://api.biteasy.io/v1/apple/{your-app-id}\`

Do not call this endpoint directly. It is intended for use by Apple only.`,
		summary: "Apple App Store Server Notifications webhook",
	})
	.input(z.object({ signedPayload: z.string(), appId: z.string() }))
	.output(z.object({ ok: z.literal(true) }));

export const googleContract = oc
	.route({
		method: "POST",
		path: "/v1/google/{appId}",
		description: `Webhook destination for Google Play Real-time Developer Notifications (RTDN)

The URL includes your BitEasy app ID. Configure this as the push endpoint for your Pub/Sub subscription:
\`https://api.biteasy.io/v1/google/{your-app-id}\`

Do not call this endpoint directly. It is intended for Google Cloud Pub/Sub push only.`,
		summary: "Google Play RTDN webhook",
	})
	.input(
		z.object({
			appId: z.string(),
			message: z.object({
				data: z.string(),
				messageId: z.string(),
				publishTime: z.string().optional(),
			}),
			subscription: z.string(),
		}),
	)
	.output(z.object({ ok: z.literal(true) }));

/**
 * Get referral link details for partner review before approval.
 */
export const referralLinkApprovalDetailsContract = oc
	.route({
		method: "GET",
		path: "/v1/referral-link-approval/{linkId}",
		description: `Fetch referral link details for partner approval.

Partners receive an email with a unique token to review and approve referral link terms.
This endpoint returns the link details and current approval status.`,
		summary: "Get referral link approval details",
	})
	.input(schemas.GetReferralLinkApprovalDetailsInputSchema)
	.output(schemas.GetReferralLinkApprovalDetailsOutputSchema);

/**
 * Partner approval of referral link terms.
 */
export const referralLinkApprovalContract = oc
	.route({
		method: "POST",
		path: "/v1/referral-link-approval/{linkId}",
		description: `Approve referral link terms.

Partners call this endpoint to approve the terms of a referral link.
Once approved, the link becomes active and financial terms are permanently locked.
This action is irreversible.`,
		summary: "Approve referral link terms",
	})
	.input(schemas.ApproveReferralLinkInputSchema)
	.output(schemas.ApproveReferralLinkOutputSchema);

export const notificationsContract = {
	list: oc
		.input(schemas.ListNotificationsInputSchema)
		.output(schemas.ListNotificationsOutputSchema),
	markRead: oc
		.input(schemas.MarkNotificationReadInputSchema)
		.output(schemas.NotificationSchema),
	markAllRead: oc.output(schemas.MarkAllNotificationsReadOutputSchema),
	count: oc.output(schemas.CountNotificationsOutputSchema),
};

export const earningsContract = {
	list: oc
		.input(schemas.ListEarningsInputSchema)
		.output(schemas.ListEarningsOutputSchema),
	export: oc
		.input(schemas.ExportEarningsInputSchema)
		.output(eventIterator(schemas.EarningSchema)),
};

export const auditContract = {
	earningsExport: oc
		.input(schemas.AuditExportInputSchema)
		.output(schemas.AuditExportOutputSchema),
};

export const payoutsContract = {
	list: oc
		.input(schemas.ListPayoutsInputSchema)
		.output(schemas.ListPayoutsOutputSchema),
	export: oc
		.input(schemas.ExportPayoutsInputSchema)
		.output(eventIterator(schemas.PayoutSchema)),
	retry: oc
		.input(schemas.RetryPayoutInputSchema)
		.output(schemas.RetryPayoutOutputSchema),
	cancelAndRetry: oc
		.input(schemas.CancelAndRetryPayoutInputSchema)
		.output(schemas.CancelAndRetryPayoutOutputSchema),
};

export const dashboardContract = {
	stats: oc
		.input(schemas.DashboardStatsInputSchema)
		.output(schemas.DashboardStatsSchema),
	revenueTimeSeries: oc
		.input(schemas.DashboardTimeSeriesInputSchema)
		.output(schemas.DashboardTimeSeriesSchema),
	attributionsTimeSeries: oc
		.input(schemas.DashboardTimeSeriesInputSchema)
		.output(schemas.DashboardTimeSeriesSchema),
	revenueByApp: oc
		.input(schemas.DashboardStatsInputSchema)
		.output(schemas.DashboardRevenueByAppSchema),
	partnerLeaderboard: oc
		.input(schemas.DashboardLeaderboardInputSchema)
		.output(schemas.DashboardPartnerLeaderboardSchema),
	payoutHealth: oc
		.input(schemas.DashboardPayoutHealthInputSchema)
		.output(schemas.DashboardPayoutHealthSchema),
	refreshCache: oc.output(z.object({ success: z.boolean() })),
};

export const billingContract = {
	getSubscription: oc.output(schemas.SubscriptionWithUsageSchema),
	createEmbeddedCheckoutSession: oc
		.input(schemas.CreateCheckoutSessionInputSchema)
		.output(schemas.CreateEmbeddedCheckoutSessionOutputSchema),
	createPortalSession: oc.output(schemas.CreatePortalSessionOutputSchema),
	updateSubscription: oc
		.input(
			z.object({
				plan: z.enum(["bootstrap", "standard", "scale"]),
			}),
		)
		.output(
			z.object({
				success: z.boolean(),
				plan: schemas.PlanIdSchema,
			}),
		),

	cancelSubscription: oc.output(
		z.object({
			success: z.boolean(),
		}),
	),
	getBillingDetails: oc.output(schemas.BillingDetailsSchema),
	listInvoices: oc
		.input(
			z.object({
				limit: z.number().min(1).max(100).default(20),
				startingAfter: z.string().optional(),
			}),
		)
		.output(schemas.InvoicesListSchema),
	createSetupIntent: oc.output(schemas.CreateSetupIntentOutputSchema),
	updateBillingAddress: oc
		.input(schemas.UpdateBillingAddressInputSchema)
		.output(z.object({ success: z.boolean() })),
	updateCompanyInfo: oc
		.input(schemas.UpdateCompanyInfoInputSchema)
		.output(z.object({ success: z.boolean() })),
};

/**
 * Public referral link approval contract (no auth required).
 */
export const publicReferralLinkApprovalContract = {
	getDetails: oc
		.input(schemas.GetReferralLinkApprovalDetailsInputSchema)
		.output(schemas.GetReferralLinkApprovalDetailsOutputSchema),
	approve: oc
		.input(schemas.ApproveReferralLinkInputSchema)
		.output(schemas.ApproveReferralLinkOutputSchema),
};

/**
 * Public referral link termination contract (no auth required).
 * Partners confirm termination via an email token.
 */
export const publicReferralLinkTerminationContract = {
	getDetails: oc
		.input(schemas.GetTerminationDetailsInputSchema)
		.output(schemas.GetTerminationDetailsOutputSchema),
	confirm: oc
		.input(schemas.ConfirmTerminationInputSchema)
		.output(schemas.ConfirmTerminationOutputSchema),
};

export const onboardingContract = {
	checklist: oc.output(schemas.OnboardingChecklistSchema),
	complete: oc.output(z.object({ success: z.boolean() })),
};

export const routerContract = {
	users: usersContract,
	apps: appsContract,
	partners: partnersContract,
	referralLinks: referralLinksContract,
	publicReferralLinkApproval: publicReferralLinkApprovalContract,
	publicReferralLinkTermination: publicReferralLinkTerminationContract,
	offerCodes: offerCodesContract,
	stripePartnerConnection: stripeConnectionContract,
	billingEntities: billingEntitiesContract,
	notifications: notificationsContract,
	earnings: earningsContract,
	payouts: payoutsContract,
	dashboard: dashboardContract,
	onboarding: onboardingContract,
	audit: auditContract,
	billing: {
		getSubscription: billingContract.getSubscription,
		createEmbeddedCheckoutSession:
			billingContract.createEmbeddedCheckoutSession,
		createPortalSession: billingContract.createPortalSession,
		updateSubscription: billingContract.updateSubscription,
		cancelSubscription: billingContract.cancelSubscription,
		getBillingDetails: billingContract.getBillingDetails,
		listInvoices: billingContract.listInvoices,
		createSetupIntent: billingContract.createSetupIntent,
		updateBillingAddress: billingContract.updateBillingAddress,
		updateCompanyInfo: billingContract.updateCompanyInfo,
	},
};

export const openApiRouterContract = {
	v1: {
		apple: appleContract,
		claim: claimContract,
		deferPreflight: deferPreflightContract,
		defer: deferContract,
		google: googleContract,
		revenuecat: revenuecatContract,
		stripeWebhook: stripeWebhookContract,
	},
};

export type RouterContract = ContractRouterClient<typeof routerContract>;
export type RouterContractInputs = InferContractRouterInputs<
	typeof routerContract
>;
export type RouterContractOutputs = InferContractRouterOutputs<
	typeof routerContract
>;
export type OpenApiRouterContract = ContractRouterClient<
	typeof openApiRouterContract
>;
export type OpenApiRouterContractInputs = InferContractRouterInputs<
	typeof openApiRouterContract
>;
export type OpenApiRouterContractOutputs = InferContractRouterOutputs<
	typeof openApiRouterContract
>;
